/**
 * widgets/scope.ts — rozřešení hierarchie scopů + node-aware volání toolu.
 *
 * Hierarchie: widget → spona → záložka → (lokál / já). Nejkonkrétnější definovaná
 * hodnota vyhrává. `undefined` = „zděď z nadřazeného", `null` = explicitní default
 * (lokál / bez zosobnění).
 */
import { mcpCall } from '../lib/mcp';
import type { EffectiveScope, ScopeProps } from './types';

/** Nejkonkrétnější definovaná hodnota vyhrává. Pořadí: nejspecifičtější první. */
export function resolveScope(...scopes: Array<ScopeProps | undefined | null>): EffectiveScope {
  let node: string | null | undefined;
  let identity: string | null | undefined;
  for (const s of scopes) {
    if (!s) continue;
    if (node === undefined && s.node !== undefined) node = s.node;
    if (identity === undefined && s.identity !== undefined) identity = s.identity;
  }
  return { node: node ?? null, identity: identity ?? null };
}

export class WidgetCallError extends Error {
  constructor(message: string, readonly kind: 'forbidden' | 'unreachable' | 'tool' = 'tool') {
    super(message);
    this.name = 'WidgetCallError';
  }
}

/**
 * Rekurzivní odbalení odpovědi mesh peer /call. Transport (MQTT/HTTP) i jednotlivé
 * tooly vrací NEKONZISTENTNÍ zanoření — pozorováno vše z:
 *   { ok, result: {call_id,…, nodes:[…]} }               (data přímo v result)
 *   { ok, result: { ok, result: { content:[{text}] } } } (dvojitá obálka)
 *   { content:[{text:'<JSON>'}] }                          (MCP content, lokál)
 * deepUnwrap prolézá `result`/`content` řetěz až k payloadu; ok:false na listu = chyba.
 */
function deepUnwrap<T>(x: unknown, depth = 0): T {
  if (depth > 6 || x === null || typeof x !== 'object') return x as T;
  const o = x as Record<string, unknown>;
  // MCP content wrapper → rozparsuj text a pokračuj
  const content = o.content as Array<{ text?: string }> | undefined;
  const txt = content?.[0]?.text;
  if (typeof txt === 'string') {
    try {
      return deepUnwrap<T>(JSON.parse(txt), depth + 1);
    } catch {
      return {} as T;
    }
  }
  // Vnořená obálka → sestup do result
  if (o.result && typeof o.result === 'object') return deepUnwrap<T>(o.result, depth + 1);
  // List payloadu: ok:false = chyba toolu
  if (o.ok === false) throw new WidgetCallError(String(o.error ?? 'Tool selhal'), 'tool');
  return o as T;
}

/**
 * Zavolá MCP tool na efektivním cíli.
 *  - node null / lokál → mcpCall (session-scoped = přihlášený uživatel)
 *  - node = peer → mesh.rpc_call (mTLS identity chain)
 * Identity (zosobnění) se propíše, až bude gate v rpc path (fáze 2); zatím
 * se volá pod vlastní identitou a 403 → placeholder.
 */
export async function callWidgetTool<T = unknown>(
  tool: string,
  args: Record<string, unknown>,
  scope: EffectiveScope,
  /** silent = polling/logy/inspect — bez loading toastu */
  opts?: { silent?: boolean; label?: string },
): Promise<T> {
  try {
    if (!scope.node) return await mcpCall<T>(tool, args, opts);
    const env = await mcpCall<{ ok?: boolean; error?: string; result?: unknown }>(
      'mesh.rpc_call',
      { node: scope.node, tool, args },
      opts,
    );
    if (env.ok === false) throw new WidgetCallError(env.error || 'Volání nodu selhalo', 'unreachable');
    return deepUnwrap<T>(env);
  } catch (e) {
    const msg = (e as Error).message || String(e);
    if (/403|forbidden|permission|scope|nemáte|zakáz/i.test(msg)) {
      throw new WidgetCallError(msg, 'forbidden');
    }
    throw e instanceof WidgetCallError ? e : new WidgetCallError(msg, 'tool');
  }
}
